/**
 * Registro de «cambios sin guardar», a nivel de módulo.
 *
 * POR QUÉ EXISTE
 * La app usa `BrowserRouter` (no un router de datos), así que no hay
 * `useBlocker`: salir de una pantalla por el menú lateral o por «Cerrar
 * sesión» desmontaba la captura de metas y se perdía lo escrito sin una sola
 * pregunta. El `beforeunload` sólo cubre cerrar o recargar la pestaña.
 *
 * Así que la pantalla que edita DECLARA aquí que tiene algo sin guardar, y el
 * layout —que no sabe nada de metas— pregunta antes de navegar o salir.
 *
 * Cada pantalla se registra con su propio `origen` para que dos que editen a
 * la vez no se borren la marca una a la otra. Al desmontarse, la pantalla
 * debe limpiar su marca (si no, el menú preguntaría para siempre).
 *
 * ⚠️ El cierre de sesión AUTOMÁTICO (token vencido sin poder renovarse) no se
 * puede detener: la sesión ya no sirve y guardar respondería 401. Eso lo avisa
 * el navegador sólo si se cierra la pestaña.
 */
const sucios = new Set<string>();

export const MENSAJE_CAMBIOS_SIN_GUARDAR =
  'Tienes cambios sin guardar. Si sales ahora se pierden. ¿Salir de todos modos?';

/** La pantalla `origen` tiene (o ya no tiene) cambios sin guardar. */
export function marcarCambiosSinGuardar(origen: string, sucio: boolean): void {
  if (sucio) sucios.add(origen);
  else sucios.delete(origen);
}

/** ¿Alguna pantalla tiene cambios sin guardar? */
export function hayCambiosSinGuardar(): boolean {
  return sucios.size > 0;
}

/** Olvida todas las marcas: tras confirmar la salida, o entre pruebas. */
export function limpiarCambiosSinGuardar(): void {
  sucios.clear();
}

/**
 * `true` = se puede salir. Si hay cambios sin guardar, pregunta con
 * `window.confirm` (síncrono a propósito: el `onClick` de un `Link` tiene que
 * decidir en el momento si cancela la navegación). Si el usuario acepta salir,
 * las marcas se olvidan: la pantalla está a punto de desmontarse.
 */
export function confirmarSalida(mensaje: string = MENSAJE_CAMBIOS_SIN_GUARDAR): boolean {
  if (!hayCambiosSinGuardar()) return true;
  const ok = typeof window !== 'undefined' && typeof window.confirm === 'function' ? window.confirm(mensaje) : true;
  if (ok) limpiarCambiosSinGuardar();
  return ok;
}
